import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Users, CalendarDays, DollarSign } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, apiError, money } from "@/lib/api";

const KPI_META = [
  ["novos_usuarios", "Novos usuários / mês", Users, "#FFAA00", false],
  ["eventos", "Eventos / mês", CalendarDays, "#FF4D00", false],
  ["gmv", "GMV (pago) / mês", DollarSign, "#22c55e", true],
  ["receita", "Receita Mí Chamou / mês", TrendingUp, "#eab308", true],
];

export default function AdminGrowth() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);
  useEffect(() => { api.get("/admin/reports/growth", { params: { months } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, [months]);
  if (!data) return <div className="text-zinc-500">Carregando...</div>;
  const fmt = (v, isMoney) => (isMoney ? money(v) : v);

  return (
    <div className="space-y-6" data-testid="admin-growth">
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-400">Janela:</span>
        {[3, 6, 12].map((m) => (
          <button key={m} data-testid={`growth-months-${m}`} onClick={() => setMonths(m)}
            className={`px-3 py-1 rounded-xl text-sm border ${months === m ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "bg-transparent border-white/10 text-zinc-400"}`}>{m} meses</button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {KPI_META.map(([k, l, Ic, color, isMoney]) => {
          const kpi = data.kpis[k];
          const up = kpi.crescimento_pct != null && kpi.crescimento_pct >= 0;
          return (
            <div key={k} data-testid={`kpi-${k}`} className="bg-card border border-white/[0.06] rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Ic className="h-3.5 w-3.5" style={{ color }} />{l}</div>
              <div className="mono text-2xl font-bold mt-1" style={{ color }}>{fmt(kpi.atual, isMoney)}</div>
              <div className="text-xs mt-1 flex items-center gap-2">
                <span className={kpi.crescimento_pct == null ? "text-zinc-500" : up ? "text-emerald-400" : "text-red-400"}>
                  {kpi.crescimento_pct == null ? "—" : `${up ? "▲" : "▼"} ${Math.abs(kpi.crescimento_pct)}%`}
                </span>
                <span className="text-zinc-600">projeção: {fmt(kpi.projecao_proximo, isMoney)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-card border border-white/[0.06] rounded-xl p-4" data-testid="chart-volume">
          <div className="text-sm font-semibold mb-3">Usuários e Eventos</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#121217", border: "1px solid #ffffff20", borderRadius: 4 }} />
              <Line type="monotone" dataKey="novos_usuarios" name="Novos usuários" stroke="#FFAA00" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="eventos" name="Eventos" stroke="#FF4D00" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-white/[0.06] rounded-xl p-4" data-testid="chart-revenue">
          <div className="text-sm font-semibold mb-3">GMV e Receita (R$)</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#121217", border: "1px solid #ffffff20", borderRadius: 4 }} formatter={(v) => money(v)} />
              <Bar dataKey="gmv" name="GMV" fill="#22c55e" radius={[3, 3, 0, 0]} />
              <Bar dataKey="receita" name="Receita" fill="#eab308" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Usuários (total)", data.totais.usuarios], ["Eventos (total)", data.totais.eventos],
          ["GMV acumulado", money(data.totais.gmv_acumulado)], ["Receita acumulada", money(data.totais.receita_acumulada)]].map(([l, v]) => (
          <div key={l} className="bg-card border border-white/[0.06] rounded-xl p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div><div className="mono text-lg font-bold">{v}</div></div>
        ))}
      </div>
    </div>
  );
}
